/** Basic spam handling's pure rules (2026-10-02; brief `docs/briefs/spam-basics.md`, draft 2). Nothing here touches a database or a model. */
export const SPAM_RETENTION_DAYS = 30;
/** who put a ticket into Spam: a member's id, or one of these */
export const SPAM_BY_SYSTEM = "system"; // the mail check (Amazon's verdict, a failed sender check)
export const SPAM_BY_AI = "ai";
export const SPAM_BY_BLOCKED = "blocked"; // the sender is on the workspace's block list

/** what a mail shows about itself beyond its words, for the classification: hints, never rules (an Outlook signature is a table too) */
export type MailFacts = { unsubscribe: boolean; remotePictures: number; tableDepth: number; links: number };
export function mailFacts(html: string | null | undefined, headers: Record<string, unknown> | null | undefined): MailFacts {
  const h = html ?? "";
  let depth = 0, tableDepth = 0;
  for (const m of h.matchAll(/<(\/?)table\b/gi)) { depth = Math.max(0, depth + (m[1] ? -1 : 1)); tableDepth = Math.max(tableDepth, depth); }
  return {
    unsubscribe: !!headers && Object.keys(headers).some((k) => k.toLowerCase() === "list-unsubscribe"),
    remotePictures: [...h.matchAll(/<img\b[^>]*\ssrc\s*=\s*["']?https?:/gi)].length,
    tableDepth,
    links: [...h.matchAll(/<a\b[^>]*\shref\s*=/gi)].length,
  };
}
export const mailFactsLine = (f: MailFacts): string => `unsubscribe header: ${f.unsubscribe ? "yes" : "no"}; pictures loaded from other servers: ${f.remotePictures}; tables nested: ${f.tableDepth} deep; links: ${f.links}`;

/** May a person's spam mark block the ticket's sender? Only a sender we can trust to be the sender (the review's B1): the ticket's first
 *  message came by mail, passed the sender check (DMARC), was not forwarded, and names a real address. A web form's address is typed
 *  by whoever fills it in; a forwarded mail's customer is read from headers; without a passed check a From line can be forged. The no-reply
 *  round (2026-11-02, finding 3): a customer read from the mail's Reply-To or a form line (`customerVia`) was typed by anyone too, so it is
 *  "unverified"; a no-reply address (a company's own form, shared by many) is refused as `no_reply` by the block itself. */
export type BlockRefusal = "form" | "unverified" | "forwarded" | "unknown_sender" | "known_customer" | "no_reply";
export function blockRefusal(first: { channel: string | null; delivery: unknown } | null | undefined): BlockRefusal | null {
  if (!first || first.channel !== "mail") return "form";
  const d = (first.delivery ?? {}) as { verdicts?: { dmarc?: string }; forwardedBy?: unknown; senderUnknown?: unknown; customerVia?: unknown };
  if (d.senderUnknown) return "unknown_sender";
  if (d.forwardedBy) return "forwarded";
  if (d.customerVia) return "unverified";
  return d.verdicts?.dmarc === "PASS" ? null : "unverified";
}
/** what the mark's toast says about the block */
export function blockWords(o: { blocked: boolean; email: string; refusal?: BlockRefusal | null }): string {
  if (o.blocked) return `Marked as spam · mail from ${o.email} goes to Spam from now on`;
  const why: Record<BlockRefusal, string> = { form: "the address was typed into the web form", unverified: "the sender's address could not be verified", forwarded: "the mail was forwarded", unknown_sender: "the mail named no sender", known_customer: "they have other tickets", no_reply: "it is a no-reply address, which forms and shops share" };
  return `Marked as spam · the sender is not blocked: ${why[o.refusal ?? "unverified"]}`;
}

/** when a spam ticket is deleted, and the words for it; counted from the moment it went to Spam */
export function spamCountdown(spamAt: string | null | undefined, now = Date.now()): string | null {
  if (!spamAt) return null;
  const d = Math.max(0, Math.ceil((Date.parse(spamAt) + SPAM_RETENTION_DAYS * 86_400_000 - now) / 86_400_000));
  return d === 0 ? "deleted today" : d === 1 ? "deleted tomorrow" : `deleted in ${d} days`;
}
/** the AI's reason for a spam flag, one word from the founder's fixed list (2026-10-22, brief knowledge-sources-round, smaller items) */
export const SPAM_REASONS = ["Phishing", "Spam", "Scam", "Bulk", "Malware", "Other"] as const;
export type SpamReason = (typeof SPAM_REASONS)[number];
/** a reason read leniently: a word of the list whatever its case, a text that starts with one gives that word ("Phishing: fake …"), any
 *  other text "Other"; nothing gives null */
export function spamReasonWord(raw: string | null | undefined): SpamReason | null {
  const t = raw?.trim();
  if (!t) return null;
  const first = /^[A-Za-z]+/.exec(t)?.[0]?.toLowerCase();
  return SPAM_REASONS.find((w) => w.toLowerCase() === first) ?? "Other";
}
/** why a ticket is in Spam, in words: the AI's reason word, the block list, the mail check, or the person who marked it */
export function spamWhy(o: { spamBy: string | null; spamReason?: string | null; memberName?: string | null }): string {
  if (o.spamBy === SPAM_BY_AI) { const word = spamReasonWord(o.spamReason); return word ? `Flagged by the AI · ${word}` : "Flagged by the AI"; }
  if (o.spamBy === SPAM_BY_BLOCKED) return "Blocked sender";
  if (o.spamBy === SPAM_BY_SYSTEM) return "Flagged by the mail check";
  return o.memberName ? `Marked by ${o.memberName}` : "Marked by a person";
}
